// Tiny typed fetch layer for the deployed API. The base URL is baked in at
// build time by the SST StaticSite (VITE_APP_API_URL). Fail fast if it's
// missing (no silent wrong-origin fetch) — matches the repo's fail-fast rule.
//
// AUTH (FE1): every request carries the user's Cognito **ID token** as a bearer
// header — its `aud` = our app-client id, exactly what the API's JWT authorizer
// validates. Best-effort: no session ⇒ no header (the gateway then 401s, which
// is correct — the UI only calls these once the user is signed in).

import { AMPLIFY_CONFIGURED } from "./lib/amplify.js";

function apiBase(): string {
  const base = import.meta.env.VITE_APP_API_URL;
  if (!base) {
    throw new Error(
      "VITE_APP_API_URL is not set. For local dev, run with VITE_APP_API_URL=<api-url>."
    );
  }
  return base.replace(/\/$/, "");
}

/** The user's ID token as a bearer header, or {} when there's no session.
 *  Best-effort — never throws (unconfigured build / anonymous → no header). */
async function authHeader(): Promise<Record<string, string>> {
  if (!AMPLIFY_CONFIGURED) return {};
  try {
    const { fetchAuthSession } = await import("aws-amplify/auth");
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/** GET `path` and parse the JSON body. Throws on a non-2xx (surfaced by the
 *  caller / React Query as an error) rather than rendering wrong/empty data. */
export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${apiBase()}${path}`, { headers: await authHeader() });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`GET ${path} failed: ${res.status} ${detail}`.trim());
  }
  return res.json() as Promise<T>;
}

/** Send a JSON body with `method` (PUT/POST/…) and parse the JSON response.
 *  Same auth as apiGet — the ID token rides along, hitting the auth: jwt routes. */
export async function apiSend<T>(
  method: "PUT" | "POST" | "PATCH" | "DELETE",
  path: string,
  body?: unknown
): Promise<T> {
  const res = await fetch(`${apiBase()}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(await authHeader()) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(`${method} ${path} failed: ${res.status} ${detail}`.trim());
  }
  return res.json() as Promise<T>;
}
